import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { CurrencyInput, DateInput, RadioInput, SelectInput } from '@/modules/base/components/inputs'
import { user } from 'ampersand-app'
import lang from '@/lang'
import moment from 'moment'
import config from '@/config'
import TextAreaInput from '@/modules/base/components/inputs/textarea-input/textarea-input'
import getVat1Germany from '@/modules/moduleSubscription/components/_helpers/getVat1Germany'
import GetLabelContent from './_helpers/getLabelContent'
import { ECONOMIC_ASSET_SPECIAL_DEPRECIATION_TYP, SPECIAL_DEPRECATION_PROPERTIES } from '@/modules/economicAsset/constants'
const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  radioInputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  subTitle: {
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.medium,
  },
  infoBoxWrapper: {
    display: 'flex',
    border: `1px solid ${theme.borderColor}`,
    padding: 10,
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    gap: 20,
  },
  info: {
    width: 25,
    height: 25,
    padding: '1px 10px',
    backgroundColor: theme.primary,
    borderRadius: 20,
  },
  infoWrapper: {
    width: 5,
    height: 19,
    fontSize: theme.fontSizes.smedium,
    color: theme.white,
  },
  optionEditContentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  dateInput: {
    width: 150,
  },
  notesContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  notesTooltip: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    fontSize: theme.fontSizes.tooltip,
  },
  notesInfo: {
    width: 12,
    height: 12,
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: theme.darkestGrey,
    borderRadius: 8,
  },
  notesInfoWrapper: {
    fontSize: theme.fontSizes.extrasmall,
    color: theme.white,
  },
  textInput: {
    '& > div > input': {
      height: '85px !important',
    },
  },
  infoText: {
    fontSize: theme.fontSizes.tooltip,
  },
  optionEditContent: {
    display: 'flex',
    flexDirection: 'row',
    gap: 30,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: 30,
  },
}))

interface AssetDisposalOption {
  label: string
  value: string
  infoText: string
}

interface VatOptions {
  label: string
  value: number
}

interface EconomicAssetBookingModalDetailsProps {
  dateOfPurchase: string
  onValuesChange: (values: { type: string, date: string | null, comments: string, vatPercent: number, priceGross: number }) => void
}

const ASSET_DISPOSAL_OPTIONS: AssetDisposalOption[] = [
  { label: lang.economicAsset.bookingModal.lostLabel, value: ECONOMIC_ASSET_SPECIAL_DEPRECIATION_TYP.LOSS_OR_DESTRUCTION, infoText: lang.economicAsset.bookingModal.infoText1 },
  { label: lang.economicAsset.bookingModal.salesLabel, value: ECONOMIC_ASSET_SPECIAL_DEPRECIATION_TYP.SALE, infoText: lang.economicAsset.bookingModal.infoText2 },
  { label: lang.economicAsset.bookingModal.withdrawalLabel, value: ECONOMIC_ASSET_SPECIAL_DEPRECIATION_TYP.TRANSFER_TO_PRIVATE_OWNERSHIP, infoText: lang.economicAsset.bookingModal.infoText3 },
]

const EconomicAssetBookingModalDetails: React.FC<EconomicAssetBookingModalDetailsProps> = (props) => {
  const vat1 = getVat1Germany()
  const VAT_OPTIONS: VatOptions[] = [
    { label: lang.onboarding.vatChoice.inputs[false], value: vat1.value },
    { label: lang.onboarding.vatChoice.inputs[true], value: 0 },
  ]
  const { dateOfPurchase, onValuesChange } = props
  const isSmallBusiness = user.isSmallBusiness
  const [type, setType] = useState<string>(ASSET_DISPOSAL_OPTIONS[0].value)
  const [date, setDate] = useState<string | null>(moment().format(config.dateFormat.api))
  const [comments, setComments] = useState<string>('')
  const [vatPercent, setVatPercent] = useState<number>()
  const [priceGross, setPriceGross] = useState<number>(0)

  const classes = useStyles()
  useEffect(() => {
    if (isSmallBusiness) {
      handleChange({ value: 0 }, SPECIAL_DEPRECATION_PROPERTIES.VAT)
    } else {
      handleChange(vat1, SPECIAL_DEPRECATION_PROPERTIES.VAT)
    }
  }, [isSmallBusiness])

  const selectedOption = useMemo(() => {
    return ASSET_DISPOSAL_OPTIONS.find(option => option.value === type)
  }, [type])

  const handleChange = (value: any, name: string) => {
    const updatedValues = { type, date, comments, vatPercent, priceGross }
    switch (name) {
      case SPECIAL_DEPRECATION_PROPERTIES.COMMENTS:
        setComments(value)
        updatedValues.comments = value
        break
      case SPECIAL_DEPRECATION_PROPERTIES.TYPE:
        setType(value)
        updatedValues.type = value
        break
      case SPECIAL_DEPRECATION_PROPERTIES.DATE:
        setDate(value)
        updatedValues.date = value
        break
      case SPECIAL_DEPRECATION_PROPERTIES.VAT: {
        setVatPercent(value.value)
        updatedValues.vatPercent = value.value
        break
      }
      case SPECIAL_DEPRECATION_PROPERTIES.PRICEGROSS: {
        setPriceGross(value)
        updatedValues.priceGross = value
        break
      }
      default:
        break
    }
    onValuesChange(updatedValues)
  }

  const assetDisposalRadioInput = (
    <div className={classes.radioInputWrapper}>
      <span className={classes.subTitle}>
        {lang.economicAsset.bookingModal.radioInputTitle}
      </span>
      <RadioInput
        name={SPECIAL_DEPRECATION_PROPERTIES.TYPE}
        options={ASSET_DISPOSAL_OPTIONS}
        onChange={handleChange}
        value={type}
      />
      <div className={classes.infoBoxWrapper}>
        <div className={classes.info}>
          <span className={classes.infoWrapper}>i</span>
        </div>
        <div className={classes.infoText}>
          {selectedOption?.infoText}
        </div>
      </div>
    </div>
  )

  const optionEditContent = (
    <div className={classes.optionEditContentWrapper}>
      <span className={classes.subTitle}>
        {lang.economicAsset.bookingModal.optionEditTitle}
      </span>
      <div className={classes.optionEditContent}>
        <div className={classes.dateInput}>
          <DateInput
            name={SPECIAL_DEPRECATION_PROPERTIES.DATE}
            value={date}
            onChange={(value) => handleChange(value, SPECIAL_DEPRECATION_PROPERTIES.DATE)}
            label={lang.economicAsset.bookingModal.datePickerLabel}
            minDate={moment(dateOfPurchase).add(1, 'day').format(config.dateFormat.api)}
            maxDate={moment().format(config.dateFormat.api)}
          />
        </div>
        {(type === ECONOMIC_ASSET_SPECIAL_DEPRECIATION_TYP.SALE || type === ECONOMIC_ASSET_SPECIAL_DEPRECIATION_TYP.TRANSFER_TO_PRIVATE_OWNERSHIP)
          && <div className={classes.row}>
            <SelectInput
              notAsync
              name={SPECIAL_DEPRECATION_PROPERTIES.VAT}
              label={<GetLabelContent type={SPECIAL_DEPRECATION_PROPERTIES.VAT} classes={classes} lang={lang} typeLabel={type} />}
              options={VAT_OPTIONS}
              value={type === ECONOMIC_ASSET_SPECIAL_DEPRECIATION_TYP.SALE ? VAT_OPTIONS[0].value : vatPercent}
              handleChange={handleChange}
              readOnly={type === ECONOMIC_ASSET_SPECIAL_DEPRECIATION_TYP.SALE || isSmallBusiness}
            />
            <CurrencyInput
              name={SPECIAL_DEPRECATION_PROPERTIES.PRICEGROSS}
              value={priceGross}
              onChange={handleChange}
              label={<GetLabelContent type={SPECIAL_DEPRECATION_PROPERTIES.PRICEGROSS} classes={classes} lang={lang} typeLabel={type} />}
            />
          </div>
        }
      </div>
    </div>
  )

  const notesContent = (
    <div>
      <TextAreaInput
        className={classes.textInput}
        label={<GetLabelContent type={SPECIAL_DEPRECATION_PROPERTIES.COMMENTS} classes={classes} lang={lang} />}
        name={SPECIAL_DEPRECATION_PROPERTIES.COMMENTS}
        value={comments}
        onChange={handleChange}
        onBlur={handleChange}
        placeholder={lang.economicAsset.bookingModal.notesPlaceHolder}
      />
    </div>
  )

  return (
    <div className={classes.content}>
      {assetDisposalRadioInput}
      {optionEditContent}
      {notesContent}
    </div>
  )
}

export default EconomicAssetBookingModalDetails
